import {
  Component,
  HostListener,
  ElementRef,
  ViewChild,
  OnInit,
} from "@angular/core";
import {
  Router,
  NavigationStart,
  NavigationEnd,
  NavigationCancel,
  NavigationError,
  ActivatedRoute,
  Scroll,
} from "@angular/router";
import { map, tap, filter } from "rxjs/operators";

import packageInfo from "../../package.json";
import { environment } from "src/environments/environment";
import { UIPermissions } from "src/auth/permissions/permissions";
import { AuthNewService } from "src/auth/services/auth-new.service";
import { inOutAnimation } from "./utils/animations/in-out.animation";
import { GlobalLoadingService } from "./services/global-loading.service";
import { ApaiEnabledService } from "./modules/apai/services/apai-enabled.service";
import { canManageDataManagement } from "./app.constants";
import { VatEnabledService } from "./modules/apai/services/vat-enabled-service";
import { EinvoicingEnabledService } from "./modules/apai/services/einvoicing-enabled.service";
import { combineLatest } from "rxjs";
import { TidioChatService } from "./services/tidio-chat.service";

type MenuMode = "vat" | "apai" | "einvoicing";

const MENU_MODE_PRIORITY: MenuMode[] = ["vat", "apai", "einvoicing"];
const MENU_MODE_LABELS: Record<MenuMode, string> = {
  vat: "VAT Reclaim",
  apai: "APAI",
  einvoicing: "E-invoicing",
};
const MENU_MODE_HOME_ROUTES: Record<MenuMode, string[]> = {
  vat: ["/app", "dashboard"],
  apai: ["/app", "apai", "dashboard"],
  einvoicing: ["/app", "e-invoicing"],
};
const MENU_MODE_GROUPS_ROUTES: Record<MenuMode, string[]> = {
  vat: ["/app", "groups"],
  apai: ["/app", "apai", "groups"],
  einvoicing: ["/app", "e-invoicing", "groups"],
};
const MENU_MODE_USERS_ROUTES: Record<MenuMode, string[]> = {
  vat: ["/app", "users"],
  apai: ["/app", "apai", "users"],
  einvoicing: ["/app", "e-invoicing", "users"],
};

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
  animations: [inOutAnimation],
})
export class AppComponent implements OnInit {
  title = "w2v-management-app";
  version = null;

  expires$ = this.auth.expires$;
  isNavZindex = false;

  open = false;
  openMenu = false;
  loading = true;
  loading$ = this.globalLoadingService.isLoading$;

  auth$ = this.auth.auth$;
  fullname$ = this.auth.auth$.pipe(
    filter((data) => !!data),
    map((auth) => auth.payload),
    map(({ firstName, lastName }) => `${lastName ?? ''}, ${firstName ?? ''}`)
  );
  menuSwitcherOpen = false;
  menuMode: MenuMode = "vat";
  availableMenuModes: MenuMode[] = [];
  readonly menuModeLabels = MENU_MODE_LABELS;
  readonly menuModeGroupsRoutes = MENU_MODE_GROUPS_ROUTES;
  readonly menuModeUsersRoutes = MENU_MODE_USERS_ROUTES;

  isApaiEnabled = false;
  isVatEnabled = true;
  isEinvoicingEnabled = false;

  get showMenuSwitcher() {
    return this.availableMenuModes.length > 1;
  }

  loggedIn = this.auth.auth$;

  @ViewChild("clickOutside") clickOutside: ElementRef;
  @ViewChild("menuSwitcher") menuSwitcher: ElementRef;

  @HostListener("document:click", ["$event.target"])
  public onClick(target) {
    const clickedInside = this.clickOutside?.nativeElement.contains(target);
    if (!clickedInside) {
      this.openMenu = false;
    }
    const clickedInsideMenuSwitcher =
      this.menuSwitcher?.nativeElement.contains(target);
    if (!clickedInsideMenuSwitcher) {
      this.menuSwitcherOpen = false;
    }
  }

  get canUseDataManagement() {
    return canManageDataManagement(this.auth.userValue?.payload);
  }
  constructor(
    private auth: AuthNewService,
    private router: Router,
    private route: ActivatedRoute,
    private globalLoadingService: GlobalLoadingService,
    private apaiEnabledService: ApaiEnabledService,
    private vatEnabledService: VatEnabledService,
    private einvoicingEnabledService: EinvoicingEnabledService,
    private tidioChatService: TidioChatService,
  ) {
    // this.mixpanel.startTracking();
    this.menuMode = this.getRequestedMenuMode(window.location.pathname);

    this.router.events.subscribe((e: any) => {
      // console.log("navigate ", e);
      if (
        e instanceof NavigationEnd &&
        (e.url === "/change-password" || e.url === "/auth/change-password")
      ) {
        this.isNavZindex = true;
      } else if (!(e instanceof Scroll)) {
        this.isNavZindex = false;
      }
      this.navigationInterceptor(e);
    });

    this.route.queryParamMap
      .pipe(
        map((params) => params.has("version")),
        filter((version) => !!version),
        tap((t) => {
          this.version = packageInfo.version;
        })
      )
      .subscribe();

    if (!environment.production) {
      this.version = packageInfo.version;
    }
  }

  ngOnInit(): void {
    this.tidioChatService.initialize();

    this.auth$.subscribe((auth) => {
      if (auth) {
        this.apaiEnabledService.fetchConfig();
        this.vatEnabledService.fetchConfig();
        this.einvoicingEnabledService.fetchConfig();
      }
    });

    combineLatest([
      this.apaiEnabledService.enabledCompanies$,
      this.vatEnabledService.enabledCompanies$,
      this.einvoicingEnabledService.enabledCompanies$,
    ]).subscribe(([apaiEnabled, vatEnabled, einvoicingEnabled]) => {
      this.isApaiEnabled = apaiEnabled?.companies?.length > 0;
      if (this.hasRole(apaiEnabled?.userRole, "Admin")) {
        this.isApaiEnabled = true;
      }
      if (this.hasRole(apaiEnabled?.userRole, "Analyst")) {
        this.isApaiEnabled = false;
      }

      this.isVatEnabled = vatEnabled?.companies?.length > 0;
      if (this.hasRole(vatEnabled?.userRole, "Admin")) {
        this.isVatEnabled = true;
      }

      this.isEinvoicingEnabled = einvoicingEnabled?.companies?.length > 0;

      if (
        !this.isVatEnabled &&
        !this.isApaiEnabled &&
        !this.isEinvoicingEnabled
      ) {
        this.isVatEnabled = true;
      }

      this.availableMenuModes = this.getAvailableMenuModes();
      this.syncMenuModeWithAvailability();
    });
  }

  getLsKey(userId: number | string) {
    return `${userId}-menu-mode`;
  }

  signOut() {
    this.auth.signOut();
  }

  navigationInterceptor(event: any): void {
    if (event instanceof NavigationStart) {
      const navState = this.router.getCurrentNavigation();
      /**preventLoader in state will prevent global loader
       * globalLoader always shown on first navigation when previousNavigation is null
       */
      if (
        !navState?.extras?.state?.["preventLoader"] ||
        !navState.previousNavigation
      )
        this.globalLoadingService.setGlobalLoading(true);
    }

    if (event instanceof NavigationEnd) {
      // this.loading = false;

      this.globalLoadingService.setGlobalLoading(false);
    }

    if (event instanceof NavigationCancel) {
      // this.loading = false;
      this.globalLoadingService.setGlobalLoading(false);
    }

    if (event instanceof NavigationError) {
      // this.loading = false;
      this.globalLoadingService.setGlobalLoading(false);
    }
  }

  onSwitchMenu(mode: MenuMode) {
    if (!this.availableMenuModes.includes(mode)) {
      return;
    }

    this.menuMode = mode;
    this.menuSwitcherOpen = false;
    this.persistMenuMode(mode);
    this.navigateToMenuMode(mode);
  }

  get production() {
    return environment.production;
  }

  get permissions() {
    return UIPermissions;
  }

  private syncMenuModeWithAvailability() {
    if (!this.availableMenuModes.length) {
      return;
    }

    const requestedMode = this.getRequestedMenuMode(window.location.pathname);
    const nextMode = this.resolveMenuMode(requestedMode);

    this.menuMode = nextMode;

    if (requestedMode !== nextMode) {
      this.navigateToMenuMode(nextMode);
    }
  }

  private getAvailableMenuModes(): MenuMode[] {
    return MENU_MODE_PRIORITY.filter((mode) => {
      switch (mode) {
        case "vat":
          return this.isVatEnabled;
        case "apai":
          return this.isApaiEnabled;
        case "einvoicing":
          return this.isEinvoicingEnabled;
      }
    });
  }

  private getRequestedMenuMode(url = this.router.url): MenuMode {
    const currentUrl = url;

    if (currentUrl.includes("/app/apai")) {
      return "apai";
    }

    if (currentUrl.includes("/app/e-invoicing")) {
      return "einvoicing";
    }

    return "vat";
  }

  private resolveMenuMode(requestedMode: MenuMode): MenuMode {
    if (this.availableMenuModes.includes(requestedMode)) {
      return requestedMode;
    }

    const savedMode = this.getSavedMenuMode();
    if (savedMode && this.availableMenuModes.includes(savedMode)) {
      return savedMode;
    }

    return this.availableMenuModes[0] || "vat";
  }

  private getSavedMenuMode(): MenuMode | null {
    const userId = this.auth.userValue?.payload?.userId;
    if (!userId) {
      return null;
    }

    const savedMode = localStorage.getItem(this.getLsKey(userId));
    if (this.isMenuMode(savedMode)) {
      return savedMode;
    }

    return null;
  }

  private persistMenuMode(mode: MenuMode) {
    const userId = this.auth.userValue?.payload?.userId;
    if (!userId) {
      return;
    }

    localStorage.setItem(this.getLsKey(userId), mode);
  }

  private navigateToMenuMode(mode: MenuMode) {
    this.router.navigate(MENU_MODE_HOME_ROUTES[mode]);
  }

  private isMenuMode(value: string | null): value is MenuMode {
    return MENU_MODE_PRIORITY.includes(value as MenuMode);
  }

  private hasRole(value: string | undefined, role: string) {
    return value?.toLowerCase() === role.toLowerCase();
  }
}
